import React from 'react';
import ReactDOM from 'react-dom/client';
import { initErrorReporting, captureError } from './sentry';
import App from './App';
import MLRODashboard from './MLRODashboard';
import JaybeeReview from './JaybeeReview';
import DirectorReview from './DirectorReview';
import SharedFile from './SharedFile';
import Attest from './Attest';
import TermsAccept from './TermsAccept';
import OrderAccept from './OrderAccept';
import { isStagingBuild, STAGING_BANNER_TEXT } from './shared/environment';
import MfaGate from './MfaGate';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e.message }; }
  componentDidCatch(e, info) { captureError(e, { extra: { componentStack: (info && info.componentStack || '').slice(0, 2000) } }); }
  render() {
    if (this.state.error) return <div style={{padding:40,fontFamily:'Arial',color:'#dc2626',fontSize:14}}>Page error: {this.state.error}</div>;
    return this.props.children;
  }
}

// Supabase password-recovery emails redirect to the project's Site URL (the root) whenever no
// allow-listed redirect applies — including every recovery sent from the Supabase dashboard.
// The root is the client onboarding app, which ignores the auth token in the URL and shows
// "Onboarding by Invitation Only", so the officer never reaches a set-password screen. Catch any
// Supabase auth callback (recovery token or auth error such as an expired link) arriving at a
// non-dashboard path and forward it, hash intact, to /mlro where the recovery handler lives.
// This runs synchronously before render so the token is not parsed and discarded here first.
(function forwardAuthCallbacksToDashboard() {
  try {
    const path = window.location.pathname;
    // Hotfix 05/10/2026: a password reset reaches the dashboard only after the
    // second factor (MfaGate), by which time the URL has been read; remember it.
    if (/(^|[#?&])type=recovery(&|$)/.test(window.location.hash) || /(^|[#?&])type=recovery(&|$)/.test(window.location.search)) window.__cidAuthRecovery = true;
    if (path === '/mlro' || path === '/mlro/') return;
    const read = (str) => new URLSearchParams((str || '').replace(/^[#?]/, ''));
    const h = read(window.location.hash);
    const s = read(window.location.search);
    const isRecovery = h.get('type') === 'recovery' || s.get('type') === 'recovery';
    const isAuthError = !!(h.get('error_code') || s.get('error_code')) ||
      h.get('error') === 'access_denied' || s.get('error') === 'access_denied';
    if (isRecovery || isAuthError) {
      window.location.replace('/mlro' + window.location.search + window.location.hash);
    }
  } catch (_) { /* never block normal routing */ }
})();

function Router() {
  const [path, setPath] = React.useState(window.location.pathname);
  
  React.useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  if (path === '/mlro' || path === '/mlro/') {
    return <ErrorBoundary><MLRODashboard /></ErrorBoundary>;
  }
  if (path === '/review') {
    return <ErrorBoundary><JaybeeReview /></ErrorBoundary>;
  }
  if (path === '/director-review') {
    return <ErrorBoundary><DirectorReview /></ErrorBoundary>;
  }
  if (path === '/shared') {
    return <ErrorBoundary><SharedFile /></ErrorBoundary>;
  }
  if (path === '/attest') {
    return <ErrorBoundary><Attest /></ErrorBoundary>;
  }
  if (path === '/terms') {
    return <ErrorBoundary><TermsAccept /></ErrorBoundary>;
  }
  if (path === '/order') {
    return <ErrorBoundary><OrderAccept /></ErrorBoundary>;
  }
  return <App />;
}

// v1_893: every page of a staging build says it is a test environment. Fixed to
// the bottom edge and click through, so it never moves or covers a control. The
// words are drawn by CSS (::after), so they are not part of the page's text: a
// tool or test that reads the page text never picks them up. Screen readers get
// the same words from the label.
const STAGING_BANNER_CSS = '[data-staging-banner]::after{content:"' + STAGING_BANNER_TEXT + '"}';
function StagingBanner() {
  if (!isStagingBuild(process.env)) return null;
  return (
    <>
      <style>{STAGING_BANNER_CSS}</style>
      <div data-staging-banner="" role="note" aria-label={STAGING_BANNER_TEXT} style={{ position:'fixed', left:0, right:0, bottom:0, zIndex:2147483000, pointerEvents:'none', background:'#f59e0b', color:'#111827', fontFamily:'Arial, sans-serif', fontSize:11, fontWeight:700, lineHeight:'18px', height:18, letterSpacing:'0.3px', textAlign:'center', padding:'0 8px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', boxShadow:'0 -1px 4px rgba(0,0,0,0.35)' }} />
    </>
  );
}
if (isStagingBuild(process.env) && typeof document !== 'undefined' && !/^TEST /.test(document.title)) document.title = 'TEST · ' + document.title;

initErrorReporting();

const root = ReactDOM.createRoot(document.getElementById('root'));
// Hotfix 05/10/2026: every page renders behind the second factor (MfaGate).
root.render(<><MfaGate><Router /></MfaGate><StagingBanner /></>);
